import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function AdminCDNStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const isCa = i18n.language === 'ca';

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/cdn/status');
      setStatus(res.data);
    } catch (error) {
      toast.error(isCa ? 'Error al carregar estat CDN' : 'Error al cargar estado CDN');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const formatSize = (mb) => mb < 1 ? `${(mb * 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;

  const truncateName = (name) => {
    if (!name) return '';
    if (name.length <= 28) return name;
    const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
    return name.substring(0, 24) + '...' + ext;
  };

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar
          title={isCa ? 'Estat CDN' : 'Estado CDN'}
          actions={
            <button onClick={fetchStatus} disabled={loading} className="btn-secondary" data-testid="cdn-refresh-btn"
              style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
              {loading ? '...' : (isCa ? 'Actualitzar' : 'Actualizar')}
            </button>
          }
        />
        <main style={{ padding: 'clamp(1rem, 4vw, 2.5rem)' }} className="fade-in">
          {loading && !status ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <div className="loading-spinner"></div>
            </div>
          ) : status ? (
            <>
              {/* Connection Status Card */}
              <div className="fintech-card" data-testid="cdn-connection-card" style={{
                marginBottom: '1.5rem',
                background: status.connected
                  ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)'
                  : status.configured
                    ? 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)'
                    : 'linear-gradient(135deg, #44403c 0%, #57534e 100%)',
                color: 'white'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h3 style={{ fontSize: 'clamp(1rem, 3.5vw, 1.25rem)', fontWeight: '700', margin: 0 }}>
                      {status.connected ? (isCa ? 'CDN Connectat' : 'CDN Conectado')
                        : status.configured ? (isCa ? 'Error de connexio' : 'Error de conexion')
                        : (isCa ? 'CDN No Configurat' : 'CDN No Configurado')}
                    </h3>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.5rem)', fontWeight: '800', lineHeight: 1 }} data-testid="cdn-total-files">
                        {status.total_files}
                      </span>
                      <div style={{ opacity: 0.85, fontSize: '0.8rem' }}>
                        {isCa ? 'fitxers' : 'archivos'}
                      </div>
                    </div>
                  </div>
                  {status.base_url && (
                    <p style={{ opacity: 0.75, fontSize: '0.8rem', margin: 0, wordBreak: 'break-all' }}>{status.base_url}</p>
                  )}
                  {status.error && (
                    <p style={{ opacity: 0.9, fontSize: '0.8rem', margin: 0, background: 'rgba(0,0,0,0.2)', padding: '0.5rem 0.75rem', borderRadius: '6px', wordBreak: 'break-word' }}>
                      {status.error}
                    </p>
                  )}
                </div>
              </div>

              {/* Folder Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '1rem' }}>
                {['contracts', 'documents', 'signatures'].map((folder) => {
                  const data = status.folders?.[folder];
                  const label = folder === 'contracts' ? (isCa ? 'Contractes' : 'Contratos')
                    : folder === 'documents' ? (isCa ? 'Documents' : 'Documentos')
                    : (isCa ? 'Signatures' : 'Firmas');

                  return (
                    <div className="fintech-card" key={folder} data-testid={`cdn-folder-${folder}`} style={{ overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem' }}>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#60a5fa', margin: 0 }}>
                          {label}
                        </h4>
                        <span style={{
                          padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', flexShrink: 0,
                          background: data ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.1)',
                          color: data ? '#34d399' : 'rgba(255,255,255,0.5)'
                        }}>
                          {data ? data.count : 0}
                        </span>
                      </div>

                      {data?.total_size_mb !== undefined && (
                        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
                          {formatSize(data.total_size_mb)}
                        </p>
                      )}

                      {data?.recent_files?.length > 0 && (
                        <div style={{ maxHeight: '160px', overflowY: 'auto', overflowX: 'hidden' }}>
                          {data.recent_files.map((f, i) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                              fontSize: '0.72rem', gap: '0.5rem', minWidth: 0
                            }}>
                              <span style={{
                                color: 'rgba(255,255,255,0.7)',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                minWidth: 0, flex: 1
                              }}>
                                {truncateName(f.name)}
                              </span>
                              <span style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, fontSize: '0.7rem' }}>
                                {f.size ? `${(f.size / 1024).toFixed(0)}K` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {data?.error && (
                        <p style={{ fontSize: '0.8rem', color: '#f87171' }}>{data.error}</p>
                      )}

                      {!data && !status.error && (
                        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>
                          {isCa ? 'Es crearà automàticament' : 'Se creará automáticamente'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}

export default AdminCDNStatus;
